// ════════════════════════════════════════════════════════════════
// REPOSITORIES — CRUD operations with encryption
// ════════════════════════════════════════════════════════════════
// These functions handle the encryption/decryption boundary.
// Form components pass plaintext; these functions encrypt before
// storing in IndexedDB and decrypt before returning to the UI.
// ════════════════════════════════════════════════════════════════

import { db } from './database';
import { getSessionKey, encryptString, decryptString } from './crypto';
import type { Collection, Item, Identifier, LedgerEntry, FormData } from '../types';
import type { FieldDefinition } from '../config/app.config';

function requireKey(): CryptoKey {
  const key = getSessionKey();
  if (!key) throw new Error('Vault is locked. Unlock first.');
  return key;
}

/**
 * Processes form data: encrypts fields marked as 'encrypted' in the
 * field definitions, converts types, and returns a clean object
 * ready for database storage.
 */
export async function processFormData(
  data: FormData,
  fields: FieldDefinition[]
): Promise<Record<string, unknown>> {
  const key = requireKey();
  const processed: Record<string, unknown> = {};

  for (const field of fields) {
    const value = data[field.key];
    if (value === undefined || value === '') {
      processed[field.key] = undefined;
      continue;
    }

    if (field.type === 'encrypted' || field.encrypted) {
      processed[field.key] = await encryptString(key, String(value));
    } else if (field.type === 'number') {
      processed[field.key] = parseFloat(String(value)) || 0;
    } else if (field.type === 'boolean') {
      processed[field.key] = Boolean(value);
    } else if (field.type === 'date') {
      processed[field.key] = value ? new Date(String(value)).getTime() : undefined;
    } else {
      processed[field.key] = value;
    }
  }

  return processed;
}

/**
 * Decrypts encrypted fields in an entity for display.
 */
export async function decryptFields(
  entity: Record<string, unknown>,
  fields: FieldDefinition[]
): Promise<Record<string, unknown>> {
  const key = requireKey();
  const result: Record<string, unknown> = { ...entity };

  for (const field of fields) {
    if ((field.type === 'encrypted' || field.encrypted) && entity[field.key]) {
      try {
        result[field.key] = await decryptString(key, String(entity[field.key]));
      } catch {
        result[field.key] = '[Decryption failed]';
      }
    }
  }

  return result;
}

// ── Collection CRUD ──

export async function createCollection(
  data: FormData,
  fields: FieldDefinition[]
): Promise<Collection> {
  const processed = await processFormData(data, fields);
  const now = Date.now();
  const id = crypto.randomUUID();

  const collection: Collection = {
    id,
    name: String(processed.name || ''),
    category: String(processed.category || ''),
    manufacturer: String(processed.manufacturer || ''),
    serialNumberEncrypted: processed.serialNumberEncrypted as string | undefined,
    purchaseDate: processed.purchaseDate as number | undefined,
    purchasePrice: processed.purchasePrice as number | undefined,
    firmwareVersion: processed.firmwareVersion as string | undefined,
    notes: String(processed.notes || ''),
    createdAt: now,
    updatedAt: now,
  };

  await db.collections.add(collection);
  return collection;
}

export async function updateCollection(
  id: string,
  data: FormData,
  fields: FieldDefinition[]
): Promise<void> {
  const processed = await processFormData(data, fields);
  await db.collections.update(id, {
    ...processed,
    name: String(processed.name || ''),
    category: String(processed.category || ''),
    manufacturer: String(processed.manufacturer || ''),
    notes: String(processed.notes || ''),
    updatedAt: Date.now(),
  });
}

export async function deleteCollection(id: string): Promise<void> {
  await db.transaction('rw', [db.collections, db.items, db.identifiers, db.ledgerEntries], async () => {
    const items = await db.items.where('collectionId').equals(id).toArray();
    for (const item of items) {
      await db.identifiers.where('itemId').equals(item.id).delete();
    }
    await db.items.where('collectionId').equals(id).delete();
    await db.ledgerEntries.where('collectionId').equals(id).delete();
    await db.collections.delete(id);
  });
}

// ── Item CRUD ──

export async function createItem(
  collectionId: string,
  data: FormData,
  fields: FieldDefinition[]
): Promise<Item> {
  const processed = await processFormData(data, fields);
  const now = Date.now();
  const id = crypto.randomUUID();

  const item: Item = {
    id,
    collectionId,
    name: String(processed.name || ''),
    symbol: String(processed.symbol || ''),
    balance: (processed.balance as number) || 0,
    purchaseDate: processed.purchaseDate as number | undefined,
    purchasePricePerUnit: processed.purchasePricePerUnit as number | undefined,
    notes: String(processed.notes || ''),
    createdAt: now,
    updatedAt: now,
  };

  await db.items.add(item);
  return item;
}

export async function updateItem(
  id: string,
  data: FormData,
  fields: FieldDefinition[]
): Promise<void> {
  const processed = await processFormData(data, fields);
  await db.items.update(id, { ...processed, updatedAt: Date.now() });
}

export async function deleteItem(id: string): Promise<void> {
  await db.transaction('rw', [db.items, db.identifiers, db.ledgerEntries], async () => {
    await db.identifiers.where('itemId').equals(id).delete();
    await db.ledgerEntries.where('itemId').equals(id).delete();
    await db.items.delete(id);
  });
}

// ── Identifier CRUD ──

export async function createIdentifier(
  itemId: string,
  data: FormData,
  fields: FieldDefinition[]
): Promise<Identifier> {
  const processed = await processFormData(data, fields);
  const now = Date.now();
  const id = crypto.randomUUID();

  const identifier: Identifier = {
    id,
    itemId,
    valueEncrypted: processed.valueEncrypted as string,
    label: String(processed.label || ''),
    isPrimary: Boolean(processed.isPrimary),
    addressType: processed.addressType as string | undefined,
    notes: String(processed.notes || ''),
    createdAt: now,
    updatedAt: now,
  };

  await db.identifiers.add(identifier);
  return identifier;
}

export async function deleteIdentifier(id: string): Promise<void> {
  await db.identifiers.delete(id);
}

// ── Ledger Entry CRUD ──

export async function createLedgerEntry(
  collectionId: string,
  data: FormData,
  fields: FieldDefinition[]
): Promise<LedgerEntry> {
  const processed = await processFormData(data, fields);
  const now = Date.now();
  const id = crypto.randomUUID();

  const entry: LedgerEntry = {
    id,
    collectionId,
    itemId: processed.itemId as string | undefined,
    entryType: String(processed.entryType || 'snapshot'),
    amount: (processed.amount as number) || 0,
    pricePerUnit: processed.pricePerUnit as number | undefined,
    totalValue: processed.totalValue as number | undefined,
    source: processed.source as string | undefined,
    date: (processed.date as number) || now,
    notes: String(processed.notes || ''),
    createdAt: now,
    updatedAt: now,
  };

  await db.ledgerEntries.add(entry);
  return entry;
}

export async function deleteLedgerEntry(id: string): Promise<void> {
  await db.ledgerEntries.delete(id);
}

// ── Encrypted Export / Import ──

export async function exportEncryptedBackup(): Promise<Blob> {
  const key = requireKey();
  const data = {
    collections: await db.collections.toArray(),
    items: await db.items.toArray(),
    identifiers: await db.identifiers.toArray(),
    ledgerEntries: await db.ledgerEntries.toArray(),
    tags: await db.tags.toArray(),
    tagAssignments: await db.tagAssignments.toArray(),
    exportedAt: Date.now(),
    version: 1,
  };

  const json = JSON.stringify(data);
  const encrypted = await encryptString(key, json);

  return new Blob([encrypted], { type: 'application/octet-stream' });
}

export async function importEncryptedBackup(file: File): Promise<void> {
  const key = requireKey();
  const text = await file.text();
  const json = await decryptString(key, text);
  const data = JSON.parse(json);

  await db.transaction(
    'rw',
    [db.collections, db.items, db.identifiers, db.ledgerEntries, db.tags, db.tagAssignments],
    async () => {
      if (data.collections) await db.collections.bulkPut(data.collections);
      if (data.items) await db.items.bulkPut(data.items);
      if (data.identifiers) await db.identifiers.bulkPut(data.identifiers);
      if (data.ledgerEntries) await db.ledgerEntries.bulkPut(data.ledgerEntries);
      if (data.tags) await db.tags.bulkPut(data.tags);
      if (data.tagAssignments) await db.tagAssignments.bulkPut(data.tagAssignments);
    }
  );
}
